import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useTemplateLoader } from '../../hooks/useTemplateLoader';
import { getMediaUrl } from '../../utils/environment';

const TemplatesSection = () => {
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAllTemplates } = useTemplateLoader();

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const allTemplates = await getAllTemplates({ limit: 12 });
        console.log('Loaded templates:', allTemplates);
        setTemplates(allTemplates);
      } catch (error) {
        console.error('Failed to load templates:', error);
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [getAllTemplates]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -294, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 294, behavior: 'smooth' });
    }
  };

  const handleTemplateClick = (templateSlug) => {
    navigate(`/editor/${templateSlug}`);
  };

  if (loading) {
    return (
      <section className="templates-section">
        <div className="temp-h">
          <h2>Free Resume Templates to</h2>
          <h2>
            <span className="edit">edit</span> and
            <span className="download">download</span> easily
          </h2>
        </div>
        <div className="loading-templates">
          <div className="spinner"></div>
          <p>Loading templates...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="templates-section">
      <div className="temp-h">
        <h2>Free Resume Templates to</h2>
        <h2>
          <span className="edit">edit</span> and
          <span className="download">download</span> easily
        </h2>
        <p className="subtitle">Click any template to start editing instantly</p>
      </div>

      {templates.length > 0 ? (
        <>
          <div className="templates-scroll">
            <button className="arrow left" onClick={scrollLeft} aria-label="Scroll left">
              <FaChevronLeft />
            </button>

            <div className="templates-wrapper" ref={scrollContainerRef}>
              {templates.map((template) => (
                <div 
                  className="template-card" 
                  key={template.id}
                  onClick={() => handleTemplateClick(template.slug)}
                  title={`Use ${template.name} template`}
                >
                  <img 
                    src={template.thumbnail_url || getMediaUrl(template.thumbnail) || '/default-template.jpg'}
                    alt={template.name} 
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = '/default-template.jpg';
                    }}
                  />
                  <div className="template-overlay">
                    <span className="template-name">{template.name}</span>
                    <span className="template-action">Use Template →</span>
                  </div>
                  {template.is_premium && (
                    <div className="premium-badge">Premium</div>
                  )}
                </div>
              ))}
            </div>

            <button className="arrow right" onClick={scrollRight} aria-label="Scroll right">
              <FaChevronRight />
            </button>
          </div>

          <div className="template-info">
            <p className="template-count">{templates.length} professional templates available</p>
            <Link to="/templates">
              <div className="explore-btn">
                <button>Explore all templates</button>
              </div>
            </Link>
          </div>
        </>
      ) : (
        <div className="no-templates">
          <p>No templates available at the moment.</p>
          <p>Please check back later or contact support.</p>
        </div>
      )}

      <style jsx>{`
        .templates-section {
          padding: 60px 20px;
          text-align: center;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }
        
        .temp-h h2 {
          font-family: 'Inter', system-ui;
          font-size: 2.5rem;
          margin: 0;
          color: #1a365d;
        }
        
        .temp-h h2:first-child {
          margin-bottom: 10px;
        }
        
        .edit {
          color: #1ca29b;
          font-weight: 700;
        }
        
        .download {
          color: #2b6cb0;
          font-weight: 700;
          margin-left: 8px;
        }
        
        .subtitle {
          color: #64748b;
          font-size: 1.1rem;
          margin-top: 15px;
          margin-bottom: 40px;
        }
        
        .templates-scroll {
          position: relative;
          max-width: 1200px;
          margin: 0 auto 40px;
        }
        
        .templates-wrapper {
          display: flex;
          gap: 20px;
          overflow-x: auto;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding: 20px 10px;
        }
        
        .templates-wrapper::-webkit-scrollbar {
          display: none;
        }
        
        .template-card {
          flex: 0 0 280px;
          height: 370px;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .template-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }
        
        .template-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .template-card:hover img {
          transform: scale(1.05);
        }
        
        .template-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%);
          padding: 20px;
          color: white;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .template-card:hover .template-overlay {
          opacity: 1;
        }
        
        .template-name {
          display: block;
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }
        
        .template-action {
          display: block;
          font-size: 14px;
          color: #1ca29b;
          font-weight: 500;
        }
        
        .premium-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          border: 2px solid #e2e8f0;
          color: #4a5568;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 10;
        }
        
        .arrow:hover {
          background: #1ca29b;
          border-color: #1ca29b;
          color: white;
          transform: translateY(-50%) scale(1.1);
        }
        
        .arrow.left {
          left: -24px;
        }
        
        .arrow.right {
          right: -24px;
        }
        
        .loading-templates, .no-templates {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #e2e8f0;
          border-top: 4px solid #1ca29b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 15px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .template-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }
        
        .template-count {
          color: #64748b;
          font-size: 16px;
          margin: 0;
        }
        
        .explore-btn button {
          background: #1ca29b;
          color: white;
          border: none;
          padding: 12px 32px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .explore-btn button:hover {
          background: #16857f;
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(28, 162, 155, 0.2);
        }
        
        @media (max-width: 768px) {
          .temp-h h2 {
            font-size: 2rem;
          }
          
          .template-card {
            flex: 0 0 250px;
            height: 330px;
          }
          
          .arrow {
            display: none;
          }
        }
        
        @media (max-width: 480px) {
          .templates-section {
            padding: 40px 15px;
          }
          
          .temp-h h2 {
            font-size: 1.75rem;
          }
          
          .template-card {
            flex: 0 0 220px;
            height: 300px;
          }
        }
      `}</style>
    </section>
  );
};

export default TemplatesSection;